import { useEffect, useContext } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';

/**
 * TokenHandler Component
 * 
 * Catches authentication token from URL query parameter 'q' after redirect
 * from the authentication service. Saves token to localStorage and triggers
 * user data refresh.
 * 
 * Flow:
 * 1. User redirected from auth service with token: https://this.codebase.com/?q={token}
 * 2. Token is extracted from 'q' query parameter
 * 3. Token is saved to localStorage with key 'token'
 * 4. AuthContext is notified to fetch user data
 * 5. URL is cleaned (token removed from query params)
 * 6. User is redirected to intended destination or dashboard
 */
const TokenHandler = ({ children }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refetchUser } = useContext(AuthContext);

  useEffect(() => {
    const token = searchParams.get('q');
    const next = searchParams.get('next');

    if (token) {
      console.log('Token received from auth service');
      
      // Save token to localStorage
      localStorage.setItem('token', token);
      
      // Mark that we just received a fresh token to prevent immediate expiration handling
      sessionStorage.setItem('freshTokenReceived', Date.now().toString());
      
      // Clean up URL by removing token and next params first
      searchParams.delete('q');
      searchParams.delete('next');
      
      // Update URL without reloading
      setSearchParams(searchParams, { replace: true });

      // Give a small delay to ensure the token is properly set before fetching user data
      setTimeout(() => {
        // Trigger user data fetch in AuthContext
        if (refetchUser) {
          refetchUser();
        }

        // If 'next' param exists, use it. Otherwise, stay on current page (don't redirect)
        if (next) {
          navigate(next, { replace: true });
        }
      }, 100);
    }
  }, [searchParams, setSearchParams, navigate, refetchUser]);

  return children;
};

export default TokenHandler;
